<template>
  <section class="home-my-bookings">
    <div class="home-column-head">
      <h5 class="font-weight-bold mb-0">{{ bookingEnabled ? 'My bookings' : 'My desk' }}</h5>
      <!-- "mine" travels with the link, so the list opens on this employee -->
      <router-link v-if="employee && bookingEnabled" :to="myBookingsLink" class="small">All my bookings</router-link>
    </div>

    <b-card v-if="loading">
      <span class="text-muted">Loading...</span>
    </b-card>

    <b-card v-else-if="loadFailed">
      <span class="text-muted">Could not load your {{ bookingEnabled ? 'bookings' : 'desk' }}.</span>
    </b-card>

    <!--
      No employee carries this user's email, so there is nothing to look their bookings up by -
      see the comment on `employee` in the script. Worth its own wording: the screen is not
      empty because they have booked nothing, and nothing they do here would change it.
    -->
    <b-card v-else-if="!employee">
      <div class="font-weight-semibold">Your account is not linked to an employee record</div>
      <div class="text-muted small mt-1">
        {{ bookingEnabled ? 'Bookings are kept against employees' : 'Desks are assigned to employees' }}, and none of
        them uses {{ user.email }}. Ask your administrator to add you to the employee directory.
      </div>
    </b-card>

    <div v-else class="home-split">
      <div class="home-stack home-split__main">
        <!-- where the reader sits next, from the building down to the desk: the permanent
             desk when there is one, since that is the answer every day -->
        <b-card v-if="place" no-body class="home-place">
          <div class="card-body">
            <div class="home-place__head">
              <div>
                <div class="home-label">{{ permanent ? 'Your permanent desk' : 'Next booking' }}</div>
                <div class="home-place__when">
                  {{ placeWhen }}<span v-if="place.timeLabel" class="text-muted"> &middot; {{ place.timeLabel }}</span>
                </div>
              </div>
              <div class="home-place__side">
                <b-badge v-if="!permanent && place.badge" :variant="place.badge.variant">
                  <i v-if="place.badge.icon" class="ion" :class="place.badge.icon" />
                  {{ place.badge.text }}
                </b-badge>
                <b-btn v-if="place.pointId" variant="outline-primary btn-sm" :to="mapLink(place)">
                  <i class="ion ion-md-locate" /> Show on map
                </b-btn>
              </div>
            </div>

            <div class="home-place__path">
              <div v-if="place.buildingName" class="home-place__step">
                <div class="home-place__key"><i class="ion ion-md-business" /> Building</div>
                <div class="home-place__value">{{ place.buildingName }}</div>
              </div>
              <div v-if="place.floorName" class="home-place__step">
                <div class="home-place__key"><i class="ion ion-md-albums" /> Floor</div>
                <div class="home-place__value">{{ place.floorName }}</div>
              </div>
              <div class="home-place__step home-place__step--desk">
                <div class="home-place__key"><i class="ion ion-md-desktop" /> Desk</div>
                <div class="home-place__value">{{ place.deskName }}</div>
              </div>
            </div>

            <!-- the office's own clock, whenever it reads differently from the viewer's -->
            <div v-if="place.floorDateLabel || place.floorTimeLabel" class="text-muted small">
              <div v-if="place.floorDateLabel"><i class="ion ion-md-globe" /> {{ place.floorDateLabel }}</div>
              <div v-if="place.floorTimeLabel"><i class="ion ion-md-globe" /> {{ place.floorTimeLabel }} office time</div>
            </div>

            <!-- the question is about this very booking, so it is asked here -->
            <HomeAsk
              v-if="placeAsk"
              :confirmation="placeAsk"
              class="home-question"
              @answered="answered(placeAsk, $event)"
              @refused="loadConfirmations"
            />
          </div>
        </b-card>

        <!-- nothing ahead: the card that would say where the reader sits offers to find somewhere -->
        <b-card v-else-if="bookingEnabled" no-body class="home-empty">
          <div class="card-body">
            <div class="home-empty__icon"><i class="ion ion-md-calendar" /></div>
            <div class="home-empty__text">
              <div class="home-label">Next booking</div>
              <div class="home-empty__title">No desk booked for the next {{ upcomingDays }} days</div>
              <div class="text-muted small">Pick a floor and a time to see which desks are free.</div>
            </div>
            <b-btn variant="primary" :to="freeDesksLink">Book a desk</b-btn>
          </div>
        </b-card>

        <b-card v-else no-body class="home-empty">
          <div class="card-body">
            <div class="home-empty__icon"><i class="ion ion-md-pin" /></div>
            <div class="home-empty__text">
              <div class="home-label">Your desk</div>
              <div class="home-empty__title">No desk assigned to you</div>
              <div class="text-muted small">Ask your administrator to place you on the map.</div>
            </div>
            <b-btn variant="primary" :to="{ name: 'map-home' }">Open map</b-btn>
          </div>
        </b-card>

        <b-card v-if="bookingEnabled && later.length" no-body class="home-later">
          <div class="card-body">
            <div class="home-caps">Then</div>
            <table class="table table-sm home-later__table">
              <tbody>
                <template v-for="row in later">
                  <tr :key="row.key" class="home-later__row">
                    <td class="home-later__date">
                      <i v-if="row.badge" class="ion ion-md-refresh text-info" :title="row.badge.text" />
                      {{ row.dayLabel || row.dateLabel }}
                      <div v-if="row.floorDateLabel" class="text-muted small">
                        <i class="ion ion-md-globe" /> {{ row.floorDateLabel }}
                      </div>
                    </td>
                    <td class="home-later__time">
                      {{ row.timeLabel || '—' }}
                      <div v-if="row.floorTimeLabel" class="text-muted small" title="Office time">
                        <i class="ion ion-md-globe" /> {{ row.floorTimeLabel }}
                      </div>
                    </td>
                    <td class="home-later__desk">
                      {{ row.floorName ? `${row.deskName} · ${row.floorName}` : row.deskName }}
                      <router-link v-if="row.pointId" :to="mapLink(row)" title="View on map" class="ml-1">
                        <i class="ion ion-md-locate"></i>
                      </router-link>
                    </td>
                  </tr>
                  <tr v-if="asks[row.key]" :key="`${row.key}-ask`" class="home-later__ask-row">
                    <td colspan="3">
                      <HomeAsk
                        :confirmation="asks[row.key]"
                        class="home-question"
                        @answered="answered(asks[row.key], $event)"
                        @refused="loadConfirmations"
                      />
                    </td>
                  </tr>
                </template>
              </tbody>
            </table>
          </div>
        </b-card>
      </div>

      <div v-if="bookingEnabled" class="home-stack home-split__side">
        <b-card no-body class="home-days">
          <div class="card-body">
            <div v-for="counter in counters" :key="counter.key" class="home-days__half">
              <div class="home-label">{{ counter.label }}</div>
              <div class="home-days__count">
                {{ counter.count }}
                <span class="home-days__unit">{{ counter.count === 1 ? 'day' : 'days' }} &middot; {{ counter.note }}</span>
              </div>
              <div class="home-days__strip" :class="`home-days__strip--${counter.key}`">
                <span
                  v-for="day in counter.strip"
                  :key="day.date"
                  class="home-days__cell"
                  :class="{ 'is-on': day.on }"
                  :title="`${day.label}${day.on ? ': in the office' : ''}`"
                ><template v-if="counter.key === 'week'">{{ day.weekday }}</template></span>
              </div>
              <!-- the list opens on the same person and the same days the number counts -->
              <router-link :to="counter.to" class="small">See these bookings</router-link>
            </div>
          </div>
        </b-card>

        <!-- the empty card above already offers it -->
        <div v-if="place">
          <b-btn variant="primary" :to="freeDesksLink">Book a desk</b-btn>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { mapActions, mapState } from 'vuex'
import HomeAsk from './HomeAsk'
import { bookingsQueryParams, filtersToQuery, KIND_SINGLE, toBookingRow } from '@/components/bookings/utils'
import {
  asksAbout,
  bookingDayLabel,
  dayStrip,
  lastWeekWindow,
  officeDaySet,
  rangeLabel,
  recentWindow,
  splitUpcoming,
  toConfirmationRow,
  UPCOMING_DAYS,
  upcomingWindow
} from './utils'
import { SERVER_DATE_TIME_FORMAT } from '@/components/desk/utils'

// one screenful of the fortnight ahead; the full list lives on /bookings
const UPCOMING_SHOWN = 5
// a fortnight of one person's bookings, so a single page holds the lot
const UPCOMING_PER_PAGE = 100

export default {
  name: 'HomeMyBookings',
  components: { HomeAsk },
  data: () => ({
    employee: null,
    permanent: null,
    upcoming: [],
    // bookings waiting on the reader to say whether they are coming
    confirmations: [],
    // read once, so the counters, their strips and their links all mean the same days
    weekWindow: lastWeekWindow(),
    monthWindow: recentWindow(),
    weekStrip: [],
    monthStrip: [],
    loading: true,
    loadFailed: false,
    upcomingDays: UPCOMING_DAYS
  }),
  computed: {
    ...mapState('auth', ['user']),
    bookingEnabled() {
      return Boolean(this.user.booking_enabled)
    },
    next() {
      return this.upcoming[0] || null
    },
    // without booking the only place anyone has is the one they were given
    place() {
      return this.permanent || (this.bookingEnabled ? this.next : null)
    },
    // each question sits on the booking it is about; one about a booking not on screen waits
    // for the bookings list or the email
    asks() {
      const pairs = this.upcoming.map((row) => [row.key, this.confirmations.find((ask) => asksAbout(ask, row))])
      return Object.fromEntries(pairs.filter(([, ask]) => ask))
    },
    placeAsk() {
      return this.permanent ? null : this.asks[this.next.key] || null
    },
    // the fortnight after the booking in the card
    later() {
      return this.permanent ? this.upcoming : this.upcoming.slice(1)
    },
    placeWhen() {
      if (this.permanent) return this.permanent.dateLabel
      return this.next.dayLabel || this.next.dateLabel
    },
    freeDesksLink() {
      // the button is a search for somewhere to sit, so it opens the tab that does that
      return { name: 'bookings', query: { tab: 'free' } }
    },
    counters() {
      const count = (strip) => strip.filter((day) => day.on).length

      return [
        {
          key: 'week',
          label: 'In the office last week',
          count: count(this.weekStrip),
          strip: this.weekStrip,
          note: rangeLabel(this.weekWindow),
          to: this.periodLink(this.weekWindow)
        },
        {
          key: 'month',
          label: 'Last 30 days',
          count: count(this.monthStrip),
          strip: this.monthStrip,
          note: rangeLabel(this.monthWindow),
          to: this.periodLink(this.monthWindow)
        }
      ]
    },
    myBookingsLink() {
      const query = this.employee ? { employee: this.employee.id } : {}
      return { name: 'bookings', query }
    }
  },
  methods: {
    ...mapActions('desk', ['deskEventsGroupedSearch', 'deskEventsSearch', 'deskEventConfirmationsSearch']),
    ...mapActions('employee', ['employeesSearch']),
    periodLink(window) {
      const query = filtersToQuery({ employees: [this.employee.id], since: window.from, until: window.to })
      return { name: 'bookings', query }
    },
    mapLink(row) {
      return { name: 'map', params: { floorId: row.floorId }, query: { pointId: row.pointId } }
    },
    /**
     * The employee whose bookings these are.
     *
     * A user and an employee are two separate records tied only by email - there is no
     * foreign key - which is the same match DeskEvent::validateOwnership() makes on the
     * server. A user can exist without one (SAML sign-in, a hand-made account), and then
     * there is nothing to show.
     */
    async loadEmployee() {
      const response = await this.employeesSearch({ params: { email: this.user.email } })
      return response.data.items[0] || null
    },
    async loadUpcoming(employee) {
      const window = upcomingWindow()
      const response = await this.deskEventsGroupedSearch({
        params: bookingsQueryParams({
          employees: [{ id: employee.id }],
          from: window.from,
          to: window.to,
          perPage: UPCOMING_PER_PAGE
        })
      })

      const { permanent, upcoming } = splitUpcoming(response.data.items)

      this.permanent = permanent ? toBookingRow(permanent) : null
      this.upcoming = upcoming.slice(0, UPCOMING_SHOWN).map((row) => ({
        ...toBookingRow(row),
        // what a confirmation is matched on - see asksAbout()
        startsAt: row.start,
        // only a single booking has one date to name; a series keeps its rule
        dayLabel: row.kind === KIND_SINGLE ? bookingDayLabel(row.start) : null
      }))
    },
    async loadConfirmations() {
      try {
        const response = await this.deskEventConfirmationsSearch()
        this.confirmations = response.data.items.map((item) => toConfirmationRow(item))
      } catch (e) {
        // Api toasts it; an unanswered question is better left off the screen than shown
        // as something the reader cannot act on - and the bookings stand without it
        this.confirmations = []
      }
    },
    async answered(ask, action) {
      this.confirmations = this.confirmations.filter((one) => one.id !== ask.id)
      // cancelling took a booking away, so what is listed here is now wrong
      if (action !== 'confirm') {
        try {
          await this.loadUpcoming(this.employee)
        } catch (e) {
          // Api toasts it; the list stays as it was until the next visit
        }
      }
    },
    /**
     * Days in the office, counted off /desk-events rather than the grouped endpoint:
     * grouping collapses a recurring series into a single row, while desk_event already
     * holds one row per occurrence - which is what distinct days have to be counted from.
     */
    async loadOfficeDays(employee) {
      const week = this.weekWindow
      const month = this.monthWindow
      const response = await this.deskEventsSearch({
        params: {
          filters: {
            employee: [employee.id],
            since: month.from.format(SERVER_DATE_TIME_FORMAT),
            until: month.to.format(SERVER_DATE_TIME_FORMAT)
          },
          pagerDisabled: 1
        }
      })

      // last week sits inside the 30-day window, so one request answers both
      const days = officeDaySet(response.data.items, month)
      this.monthStrip = dayStrip(days, month)
      this.weekStrip = dayStrip(days, week)
    },
    async load() {
      this.loading = true
      try {
        const employee = await this.loadEmployee()
        this.employee = employee
        if (employee) {
          const loads = [this.loadUpcoming(employee)]
          if (this.bookingEnabled) loads.push(this.loadOfficeDays(employee), this.loadConfirmations())
          await Promise.all(loads)
        }
        this.loadFailed = false
      } catch (e) {
        // Api already toasts the failure; the panel only has to stop claiming a result
        this.employee = null
        this.permanent = null
        this.upcoming = []
        this.loadFailed = true
      } finally {
        this.loading = false
      }
    }
  },
  created() {
    this.load()
  }
}
</script>

<style scoped lang="scss">
// the theme's primary (theme-twitlight); the panel tints with it rather than adding a colour
$primary: #4c84ff;
$line: rgba(24, 28, 33, 0.08);
$hair: rgba(24, 28, 33, 0.06);
$primary-soft: rgba($primary, 0.1);
$muted: #a3a4a6;

.home-column-head {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  margin-bottom: 1rem;
}

// side by side while the panel has room for both, one above the other once it has not
.home-split {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  gap: 1rem;

  &__main {
    flex: 3 1 560px;
    min-width: 0;
  }

  &__side {
    flex: 2 1 360px;
    min-width: 0;
  }
}

.home-stack {
  display: grid;
  gap: 1rem;
}

.home-label {
  color: #888;
  font-size: 0.85rem;
}

.home-caps {
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: $muted;
  font-weight: 500;
}

// a question tinted apart from the booking it sits on
.home-question {
  background: #fff8e8;
  border: 1px solid #f6dfae;
  border-radius: 4px;
  padding: 0.6rem 0.9rem;
}

// --- where the reader sits next ---------------------------------------------

.home-place {
  .card-body {
    display: grid;
    gap: 1rem;
  }

  &__head {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 0.75rem;
  }

  &__side {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  &__when {
    font-size: 1.05rem;
    font-weight: 500;
    font-variant-numeric: tabular-nums;
  }

  // building -> floor -> desk, general to particular; the desk is the one the reader is after
  &__path {
    display: grid;
    grid-auto-flow: column;
    grid-auto-columns: minmax(0, 1fr);
    border: 1px solid $line;
    border-radius: 4px;
    overflow: hidden;
  }

  &__step {
    padding: 0.6rem 0.9rem;
    min-width: 0;

    & + & {
      border-left: 1px solid $line;
    }

    &--desk {
      background: $primary-soft;

      .home-place__value {
        font-size: 1.4rem;
        font-weight: bold;
        line-height: 1.2;
        color: $primary;
      }
    }
  }

  &__key {
    font-size: 0.72rem;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: $muted;

    .ion {
      margin-right: 0.2rem;
    }
  }

  &__value {
    font-weight: 500;
    overflow-wrap: anywhere;
  }

  @media (max-width: 575px) {
    &__path {
      grid-auto-flow: row;
    }

    &__step + &__step {
      border-left: 0;
      border-top: 1px solid $line;
    }
  }
}

// --- nothing booked ------------------------------------------------------------

.home-empty {
  .card-body {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 1rem 1.25rem;
  }

  &__icon {
    flex: none;
    width: 3rem;
    height: 3rem;
    border-radius: 50%;
    background: $primary-soft;
    color: $primary;
    font-size: 1.5rem;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &__text {
    flex: 1 1 220px;
  }

  &__title {
    font-size: 1.05rem;
    font-weight: 500;
    margin: 0.1rem 0 0.15rem;
  }
}

// --- the rest of the fortnight -----------------------------------------------------

.home-later {
  &__table {
    margin: 0.25rem 0 0;
    font-variant-numeric: tabular-nums;

    td {
      padding: 0.45rem 0.75rem 0.45rem 0;
      vertical-align: top;
      border-top: 1px solid $hair;
    }

    tr:first-child td {
      border-top: 0;
    }

    td:last-child {
      padding-right: 0;
    }
  }

  &__date {
    font-weight: 500;
    overflow-wrap: anywhere;
  }

  &__time {
    white-space: nowrap;
  }

  &__desk {
    overflow-wrap: anywhere;
  }

  &__ask-row td {
    border-top: 0;
    padding-top: 0;
  }
}

// --- days in the office ----------------------------------------------------------

.home-days {
  .card-body {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 1rem 1.5rem;
  }

  &__half {
    display: grid;
    gap: 0.35rem;
    align-content: start;
  }

  &__count {
    font-size: 1.25rem;
    font-weight: bold;
    line-height: 1.2;
    font-variant-numeric: tabular-nums;
  }

  &__unit {
    font-size: 0.8rem;
    font-weight: normal;
    color: $muted;
  }

  // one cell a day, filled for a day in the office
  &__strip {
    display: flex;
    gap: 3px;
  }

  &__cell {
    flex: 1 1 0;
    min-width: 0;
    border-radius: 2px;
    background: rgba(24, 28, 33, 0.06);

    &.is-on {
      background: $primary;
    }
  }

  // a week is seven days that each need naming
  &__strip--week &__cell {
    height: 1.35rem;
    font-size: 0.68rem;
    line-height: 1.35rem;
    text-align: center;
    color: $muted;

    &.is-on {
      color: #fff;
    }
  }

  // thirty are a rhythm, read at a glance
  &__strip--month &__cell {
    height: 1.35rem;
  }
}
</style>
